import { useEffect, useState } from 'react';
import { NAZIVI_STANJA, NAZIVI_VEZE, opisGodine } from '../lib/konstante';
import { useKontakti } from '../hooks/usePodaci';
import UnosKoordinata from './UnosKoordinata';
import StatusTocke from './StatusTocke';
import PotvrdaUklanjanja from './PotvrdaUklanjanja';
import PovijestKucanstva from './PovijestKucanstva';
import { vrijemePromjene } from '../lib/povijest';

function prikazTelefona(telefon) {
  const z = String(telefon);
  if (/^0\d{9}$/.test(z))
    return `${z.slice(0, 3)} ${z.slice(3, 6)} ${z.slice(6)}`;
  return z;
}

/**
 * Kartica odabranog kućanstva. Pozicija se postavlja isključivo upisom
 * koordinata i klikom na Potvrdi.
 */
export default function KarticaKucanstva({
  kucanstvo,
  jeUprava,
  mozeUredjivati = false,
  naUredjivanje,
  dokumenti = [],
  dokumentiGreska,
  ucitavaDokumente,
  naDokumente,
  onSpremi,
  onPotvrdi,
  onUkloni,
  onZatvori,
}) {
  const {
    kontakti,
    ucitava: ucitavaKontakte,
    greska: greskaKontakata,
  } = useKontakti(kucanstvo?.id ?? null);
  const [radi, setRadi] = useState(false);
  const [poruka, setPoruka] = useState(null);
  const [uklanja, postaviUklanja] = useState(false);

  useEffect(() => {
    setPoruka(null);
  }, [kucanstvo?.id]);

  if (!kucanstvo) return null;

  const pozovi = async (fn) => {
    setRadi(true);
    try {
      await fn(kucanstvo);
      postaviUklanja(false);
      setPoruka({ vrsta: 'ok', tekst: 'Promjena je spremljena.' });
    } catch (e) {
      setPoruka({ vrsta: 'greska', tekst: e.message });
    } finally {
      setRadi(false);
    }
  };

  const napomene = [];
  if (kucanstvo.status === 'predlozeno')
    napomene.push(
      jeUprava
        ? 'Točka je automatski geokodirana i nije provjerena. Usporedi s ortofotom pa potvrdi ili upiši točne koordinate.'
        : 'Pozicija je automatski predložena. Uprava još treba provjeriti njezinu točnost.',
    );
  if (kucanstvo.status === 'ne')
    napomene.push(
      jeUprava
        ? 'Nema pozicije. Upiši koordinate i potvrdi poziciju.'
        : 'Pozicija priključka još nije upisana.',
    );

  const nositelj = [kucanstvo.nositelj_prezime, kucanstvo.nositelj_ime]
    .filter(Boolean)
    .join(' ');

  return (
    <section className="kartica" aria-label="Detalji kućanstva">
      {kucanstvo.stanje === 'nejasno' && (
        <div className="nejasno">
          {kucanstvo.napomena || 'Stanje je nejasno — treba provjeriti.'}
        </div>
      )}

      <header>
        <div>
          <h3>{kucanstvo.adresa ?? kucanstvo.naselje}</h3>
          <small>{nositelj || 'nositelj nije upisan'}</small>
          <StatusTocke status={kucanstvo.status} />
        </div>
        <button className="zatvori" onClick={onZatvori} aria-label="Zatvori">
          ×
        </button>
      </header>

      <div className="autorstvo-kucanstva">
        <p>Unio/la: <strong>{kucanstvo.unio_naziv || (kucanstvo.unio ? 'Korisnik bez zabilježenog naziva' : 'Autor nije zabilježen (raniji unos)')}</strong></p>
        {kucanstvo.created_at && <time dateTime={kucanstvo.created_at}>{vrijemePromjene(kucanstvo.created_at)}</time>}
      </div>

      {mozeUredjivati && (
        <div className="radnje-kartice">
          <button className="gumb sek" onClick={naUredjivanje}>
            Uredi kućanstvo i kontakte
          </button>
        </div>
      )}
      {mozeUredjivati && (
        <div className="dokumenti-kucanstva">
          <h4>Dokumenti i GDPR privola</h4>
          <p>
            {ucitavaDokumente
              ? 'Učitavam priloge…'
              : dokumentiGreska
                ? 'Prilozi se nisu učitali.'
                : dokumenti.some((d) => d.vrsta === 'gdpr_privola')
                  ? `Priložena privola · ${dokumenti.filter((d) => d.vrsta === 'gdpr_privola').length} datoteka`
                  : 'GDPR privola još nije priložena.'}
          </p>
          <button className="gumb sek" onClick={naDokumente}>
            Otvori / dodaj dokumente{' '}
            {dokumenti.length > 0 && `(${dokumenti.length})`}
          </button>
        </div>
      )}
      <dl className="podaci">
        <div>
          <dt>Izvor</dt>
          <dd>
            {kucanstvo.izvor === 'uvoz'
              ? 'Uvoz iz evidencije'
              : 'Terenski unos'}
          </dd>
        </div>
        <div>
          <dt>Priključaka</dt>
          <dd>{kucanstvo.broj_prikljucaka}</dd>
        </div>
        <div>
          <dt>Priključeno</dt>
          <dd>{opisGodine(kucanstvo.godina_prikljucenja)}</dd>
        </div>
        <div>
          <dt>Stanje</dt>
          <dd>{NAZIVI_STANJA[kucanstvo.stanje] ?? kucanstvo.stanje}</dd>
        </div>
        {kucanstvo.lat != null && (
          <div>
            <dt>Koordinate</dt>
            <dd className="koordinate-prikaz">
              {Number(kucanstvo.lat).toFixed(6)},{' '}
              {Number(kucanstvo.lng).toFixed(6)}
            </dd>
          </div>
        )}
        {kucanstvo.opis_lokacije && (
          <div>
            <dt>Opis lokacije</dt>
            <dd>{kucanstvo.opis_lokacije}</dd>
          </div>
        )}
      </dl>

      {ucitavaKontakte && (
        <p className="telo" role="status">
          Učitavam dostupne kontakte…
        </p>
      )}
      {greskaKontakata && (
        <p className="telo poruka greska" role="alert">
          Kontakti se nisu učitali. {greskaKontakata}
        </p>
      )}

      {kontakti.length > 0 && (
        <div className="kontakti">
          <h4>Kontakti</h4>
          {kontakti.map((k) => (
            <div key={k.id} className="kontakt">
              <p>
                {k.ime} {k.prezime} · {NAZIVI_VEZE[k.veza] ?? k.veza}
                {k.glavni && (
                  <span className="glavni" title="Glavni kontakt">
                    {' '}
                    ★
                  </span>
                )}
              </p>
              {k.telefon && (
                <p>
                  <a href={`tel:${k.telefon}`}>{prikazTelefona(k.telefon)}</a>
                  {k.telefon_nositelja ? ' (nositeljev)' : ''}
                </p>
              )}
              {k.email && (
                <p>
                  <a href={`mailto:${k.email}`}>{k.email}</a>
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {napomene.length > 0 && (
        <div className="telo">
          {napomene.map((t, i) => (
            <p key={i}>{t}</p>
          ))}
        </div>
      )}

      {jeUprava ? (
        <UnosKoordinata
          lat={kucanstvo.lat}
          lng={kucanstvo.lng}
          zauzeto={radi}
          onPotvrdi={(la, ln) => onSpremi(kucanstvo, la, ln)}
        >
          {['predlozeno', 'smjesteno'].includes(kucanstvo.status) && (
            <button
              className="sek"
              onClick={() => pozovi(onPotvrdi)}
              disabled={radi}
            >
              Pozicija je točna
            </button>
          )}
          {kucanstvo.lat != null && (
            <button
              className="sek"
              onClick={() => postaviUklanja(true)}
              disabled={radi}
            >
              Ukloni
            </button>
          )}
        </UnosKoordinata>
      ) : (
        <div className="telo zakljucano">
          Postavljanje pozicije dostupno je upravi.
        </div>
      )}
      {uklanja && jeUprava && (
        <PotvrdaUklanjanja
          radi={radi}
          naOdustani={() => postaviUklanja(false)}
          naPotvrdu={() => pozovi(onUkloni)}
        />
      )}

      {jeUprava && poruka && (
        <div className="unos">
          <p
            className={`poruka ${poruka.vrsta}`}
            role={poruka.vrsta === 'greska' ? 'alert' : 'status'}
          >
            {poruka.tekst}
          </p>
        </div>
      )}

      {kucanstvo.napomena && kucanstvo.stanje !== 'nejasno' && (
        <div className="telo">
          <p>Napomena: {kucanstvo.napomena}</p>
        </div>
      )}
      {mozeUredjivati && <PovijestKucanstva kucanstvo={kucanstvo} />}
    </section>
  );
}
